import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { styled } from '@components-shared/src/stitches';
import { LoadingBars } from '@components/LoadingBars/LoadingBars';
import { Header, Modal } from '@components/main-components/modals/Modal';
import { useMintReferral } from '@context/mintReferral';
import { ModalContext } from '@context/modal';
import { Transaction } from '@context/transaction';
import { assertIsHex } from '@utils/hex';
import { sendReferralSourceIfPresent } from './Buy/ReferralSource';

export const PaperProcessingModal = ({
  receiptId,
  releaseId,
  chainId,
}: {
  receiptId: string;
  releaseId: string;
  chainId: number;
}) => {
  const { closeCancelModal } = ModalContext.useContainer();
  const { safeHandleTransaction } = Transaction.useContainer();
  const { referral } = useMintReferral();
  const { data: transactionHash } = useQuery({
    queryFn: async () => {
      const request = await fetch(`https://withpaper.com/api/v1/transaction-status/${receiptId}`);
      const parsedJson = (await request.json()) as {
        result?: {
          transactionHash?: string;
          transactionId: string;
        };
      };

      return parsedJson?.result?.transactionHash;
    },
    queryKey: ['paper-processing-modal', receiptId],
    enabled: !!receiptId,
    refetchInterval: data => {
      // once we have the hash we can stop polling
      if (data) return false;
      // otherwise poll every 2 seconds
      return 2000;
    },
  });

  React.useEffect(() => {
    if (!transactionHash) {
      return;
    }

    assertIsHex(transactionHash);

    sendReferralSourceIfPresent({
      txHash: transactionHash,
      chainId,
      referral,
    });
    safeHandleTransaction({
      transactionFn: async () => transactionHash,
      contractMethod: 'SAM__BUY_EDITION',
      releaseId,
      showOpenWalletToast: false,
      chainId,
      openProcessingModal: true,
    });
  }, [chainId, referral, releaseId, safeHandleTransaction, transactionHash]);

  const body = (
    <>
      <ImageContainer>
        <LoadingBars height={undefined} />
      </ImageContainer>
      <Header css={{ justifyContent: 'center', marginTop: 8, '@tablet': { marginTop: 16 } }}>
        Processing...
      </Header>
      <TextContent>
        <Text>Hang tight! We're preparing</Text>
        <Text>your transaction now.</Text>
      </TextContent>
    </>
  );

  return (
    <Modal
      body={body}
      closeModal={closeCancelModal}
      headerStyles={{ height: 0 }}
      bodyStyles={{ marginTop: 0 }}
    />
  );
};

const ImageContainer = styled('div', {
  width: '100%',
  marginBottom: 8,
  display: 'flex',
  justifyContent: 'center',
});

const TextContent = styled('div', {
  textAlign: 'center',
  fontSize: 14,
  marginTop: 8,
  '@tablet': {
    fontSize: 16,
    marginTop: 16,
  },
});

const Text = styled('div', {
  color: '$neutral700',
  lineHeight: '20px',
  fontWeight: 400,
  a: {
    fontWeight: 500,
  },
});
